import { settingsActions } from "@src/store";
import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { clearPasswordManagerAuthTokens } from "@src/utils/passwordManagerAuth";
import { isTokenExpired, saveReturnUrl } from "@src/utils/session.utils";
import { resetSessionExpiredFlag } from "@src/utils/axiosInterceptor";

const AuthContext = createContext<{
	isAuthenticated: boolean;
	login: (token: string, rememberMe: boolean, hideSideBar: boolean) => void;
	logout: () => void;
	rememberMe: boolean;
	setRememberMe: React.Dispatch<React.SetStateAction<boolean>>;
}>({
	isAuthenticated: false,
	login: (_token: string, _rememberMe: boolean, _hideSideBar: boolean) => {
		// intentionally left blank
	},
	logout: () => {
		// intentionally left blank
	},
	rememberMe: false,
	setRememberMe: () => {
		// intentionally left blank
	},
});

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [rememberMe, setRememberMe] = useState(false);
	const token = useAppSelector((state) => state.settings.token);

	const appDispatch = useAppDispatch();
	const navigate = useNavigate();

	const login = (token: string, rememberMe: boolean, hideSideBar: boolean) => {
		resetSessionExpiredFlag();
		appDispatch(settingsActions.setPortalSidebarIsHidden(hideSideBar));
		appDispatch(settingsActions.setToken({ token, rememberMe }));
	};

	const logout = useCallback(() => {
		saveReturnUrl();
		clearPasswordManagerAuthTokens();
		appDispatch(settingsActions.clearToken());
		navigate("/login");
	}, [appDispatch, navigate]);

	// Validate token: treat empty or expired tokens as unauthenticated
	const isAuthenticated = !!token && !isTokenExpired(token);

	// On mount (or when token changes) – if the stored token is expired,
	// clear it immediately so the app doesn't stay in a "zombie" state.
	useEffect(() => {
		if (token && isTokenExpired(token)) {
			logout();
		}
	}, [token, logout]);

	// Listen for 401 responses from ApiProvider's axios interceptor.
	// When the API signals an expired/invalid session we automatically
	// redirect to login while preserving the current deep-link.
	useEffect(() => {
		const handleSessionExpired = () => {
			logout();
		};

		window.addEventListener("auth:session-expired", handleSessionExpired);
		return () => {
			window.removeEventListener("auth:session-expired", handleSessionExpired);
		};
	}, [logout]);

	return (
		<AuthContext.Provider value={{ isAuthenticated, login, logout, rememberMe, setRememberMe }}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
